import Image from "next/image";
import { CyberCertifications } from "@/components/cyber-certifications";

import { DISPLAY } from "@/components/editorial";
import { docsHref } from "@/lib/docs-links";
import { EuFlag, UkFlag, UsFlag } from "./drawing-variant-regions";

const INK_66 = "text-[color-mix(in_srgb,var(--theme-ink)_66%,transparent)]";
const INK_80 = "text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]";

/** Where a Twin's server can run, with each region's status today. */
const REGIONS = [
  { name: "European Union", status: "Available today", Flag: EuFlag },
  { name: "United States", status: "Available today", Flag: UsFlag },
  { name: "United Kingdom", status: "In development", Flag: UkFlag },
] as const;

/**
 * What holds wherever it runs, in the claims the FAQ already makes. The
 * server is the one that depends on the plan: Team, the per-user plan, runs
 * on a shared deployment, which the band's lede says.
 */
const FACTS = [
  { title: "Dedicated server", body: "On Core, Scale and Enterprise, your Twin runs on a server dedicated to your organisation." },
  { title: "Read-only connections", body: "It reads the systems you connect without changing the original records." },
  { title: "Your model provider", body: "Requests go to the AI provider configured for your organisation." },
  { title: "Audit trail", body: "Checked answers show the definition, version and sources they used." },
] as const;

/**
 * Hosting and trust as open facts (the 2026-09-30 copy review): where the
 * server can run, as a static row with each region's status; what holds
 * wherever it runs; and the Crown Commercial Service supplier mark and the
 * Legal & Security pages. The fully UK-hosted Twin, in development, has its
 * own band below this one since 2026-10-01 (landing-uk-story).
 */
export function HostingInfrastructure() {
  return (
    <>
      <ul
        aria-label="Where your server can run"
        className="m-0 grid list-none gap-5 p-0 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-dotted sm:divide-[var(--rule)]"
      >
        {REGIONS.map(({ name, status, Flag }) => (
          <li key={name} className="flex items-center gap-4 sm:px-6 sm:first:pl-0">
            <svg viewBox="0 0 100 66.67" className="h-7 w-[2.625rem] shrink-0" aria-hidden="true">
              <Flag x={0} y={0} width={100} height={66.67} />
            </svg>
            <div>
              <p style={DISPLAY} className="text-[1.05rem] font-semibold tracking-[-0.02em] text-[var(--theme-ink)]">
                {name}
              </p>
              <p className={`text-[0.82rem] ${INK_66}`}>{status}</p>
            </div>
          </li>
        ))}
      </ul>
      <p className={`mt-4 max-w-[72ch] text-[0.82rem] leading-[1.6] ${INK_66}`}>
        The region is where your server runs. Model processing can take place elsewhere, depending on the provider you use.
      </p>
      <dl className="mt-10 grid gap-x-8 gap-y-6 border-t border-dotted border-[var(--rule)] pt-8 sm:grid-cols-2 lg:grid-cols-4">
        {FACTS.map(({ title, body }) => (
          <div key={title}>
            <dt style={DISPLAY} className="text-[1rem] font-semibold tracking-[-0.01em] text-[var(--theme-ink)]">
              {title}
            </dt>
            <dd className={`mt-1.5 text-[0.92rem] leading-[1.55] ${INK_80}`}>{body}</dd>
          </div>
        ))}
      </dl>
      <div id="security-certifications" className="mt-10 scroll-mt-24 border-t border-dotted border-[var(--rule)] pt-8">
        <CyberCertifications />
      </div>
      <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-dotted border-[var(--rule)] pt-6 text-[0.88rem]">
        <p className={`flex items-center gap-3 ${INK_80}`}>
          <Image src="/logos/light/ccs.png" alt="" width={1177} height={878} className="h-9 w-auto" />
          Mercury Labs is a Crown Commercial Service supplier.
        </p>
        <a href={docsHref("/policies")} className="font-medium text-[var(--theme-ink)] underline underline-offset-4">
          Legal &amp; Security <span aria-hidden="true">&rarr;</span>
        </a>
      </div>
    </>
  );
}
